import * as SecureStore from 'expo-secure-store';
import { bytesToHex, hexToBytes } from '@noble/ciphers/utils.js';
import { DEFAULT_KDF, deriveKey, generateSalt, type KdfParams } from './backupCrypto';

/**
 * Guarda a chave de backup derivada da senha (e o sal/params) no SecureStore.
 *
 * Derivamos a chave UMA vez (quando o usuario define a senha) e cacheamos a chave
 * — nao a senha — pra que os backups automaticos (fase 2) sejam silenciosos sem
 * rodar o scrypt toda vez. O sal e os params tambem vao embutidos em cada arquivo,
 * pra permitir restaurar em outro aparelho digitando a senha.
 */

const KEY_HEX = 'kontto_backup_key';
const SALT_HEX = 'kontto_backup_salt';
const PARAMS_JSON = 'kontto_backup_kdf';

export async function hasBackupPassphrase(): Promise<boolean> {
    return (await SecureStore.getItemAsync(KEY_HEX)) != null;
}

export async function setBackupPassphrase(passphrase: string): Promise<void> {
    const salt = generateSalt();
    const key = await deriveKey(passphrase, salt, DEFAULT_KDF);
    await SecureStore.setItemAsync(KEY_HEX, bytesToHex(key));
    await SecureStore.setItemAsync(SALT_HEX, bytesToHex(salt));
    await SecureStore.setItemAsync(PARAMS_JSON, JSON.stringify(DEFAULT_KDF));
}

export async function clearBackupPassphrase(): Promise<void> {
    await SecureStore.deleteItemAsync(KEY_HEX);
    await SecureStore.deleteItemAsync(SALT_HEX);
    await SecureStore.deleteItemAsync(PARAMS_JSON);
}

export async function getBackupKey(): Promise<Uint8Array | null> {
    const hex = await SecureStore.getItemAsync(KEY_HEX);
    return hex ? hexToBytes(hex) : null;
}

export async function getBackupSalt(): Promise<Uint8Array | null> {
    const hex = await SecureStore.getItemAsync(SALT_HEX);
    return hex ? hexToBytes(hex) : null;
}

export async function getBackupKdfParams(): Promise<KdfParams> {
    const raw = await SecureStore.getItemAsync(PARAMS_JSON);
    if (!raw) return DEFAULT_KDF;
    try {
        return JSON.parse(raw) as KdfParams;
    } catch {
        return DEFAULT_KDF;
    }
}

/** Confere se a senha digitada bate com a configurada neste aparelho. */
export async function verifyBackupPassphrase(passphrase: string): Promise<boolean> {
    const stored = await SecureStore.getItemAsync(KEY_HEX);
    const salt = await getBackupSalt();
    if (!stored || !salt) return false;
    const params = await getBackupKdfParams();
    const key = await deriveKey(passphrase, salt, params);
    return bytesToHex(key) === stored;
}
